import {FC, useEffect, useLayoutEffect, useRef, useState} from "react";

const KEYFRAMES_ID = '__sb_marquee_keyframes__';

const ensureKeyframes = () => {
    if (typeof document === 'undefined') return;
    if (document.getElementById(KEYFRAMES_ID)) return;
    const style = document.createElement('style');
    style.id = KEYFRAMES_ID;
    style.textContent = `@keyframes sb-marquee-scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`;
    document.head.appendChild(style);
};

type Props = {
    text: string;
    fontSize?: number;
    pxPerSecond?: number;
    gapPx?: number;
};

const MarqueeText: FC<Props> = ({text, fontSize = 10, pxPerSecond = 30, gapPx = 24}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const [overflow, setOverflow] = useState(false);
    const [duration, setDuration] = useState(8);

    useEffect(() => { ensureKeyframes(); }, []);

    useLayoutEffect(() => {
        const measure = () => {
            const c = containerRef.current;
            const m = measureRef.current;
            if (!c || !m) return;
            const w = m.scrollWidth;
            const overflows = w > c.clientWidth + 1;
            setOverflow(overflows);
            if (overflows) setDuration(Math.max(4, w / pxPerSecond));
        };
        measure();
        const ro = new ResizeObserver(measure);
        if (containerRef.current) ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, [text, pxPerSecond]);

    return (
        <div
            ref={containerRef}
            title={text}
            style={{
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                width: '100%',
                minWidth: 0,
                fontSize,
                position: 'relative',
            }}
        >
            <span
                ref={measureRef}
                style={{
                    position: 'absolute',
                    visibility: 'hidden',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                }}
            >
                {text}
            </span>
            {overflow ? (
                <div
                    style={{
                        display: 'inline-block',
                        whiteSpace: 'nowrap',
                        animation: `sb-marquee-scroll ${duration}s linear infinite`,
                    }}
                >
                    <span style={{paddingRight: gapPx}}>{text}</span>
                    <span style={{paddingRight: gapPx}}>{text}</span>
                </div>
            ) : (
                <span>{text}</span>
            )}
        </div>
    );
};

export default MarqueeText;
